'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type Player from 'video.js/dist/types/player';
import 'video.js/dist/video-js.css';

import { detectVideoTypeFromSource } from '@/app/elements/video/video.utils';
import { useIntersectionObserver } from '@/hooks/use-intersection-observer';
import { classes } from '@/utils/classes/classes';

import { AnimatedTilePhase, type AnimatedTileProps } from './animated-tile.types';

import './styles/default.scss';

const PLAYER_DEFAULT_OPTIONS = {
    autoplay: 'muted' as const,
    muted: true,
    loop: true,
    controls: false,
    playsinline: true,
    fluid: false,
    fill: true
};

export const AnimatedTile = ({ videoSrc, children, gameName, priority = false }: AnimatedTileProps) => {
    const [phase, setPhase] = useState<AnimatedTilePhase>(AnimatedTilePhase.LOADING);
    const [rootEl, setRoot] = useState<HTMLDivElement | null>(null);
    const [observeRef, isIntersecting] = useIntersectionObserver<HTMLDivElement>();
    const playerRef = useRef<Player | null>(null);

    const tileRef = useCallback(
        (node: HTMLDivElement | null) => {
            setRoot(node);
            observeRef(node);
        },
        [observeRef]
    );

    const attachPlayerListeners = (player: Player, isCancelled: () => boolean) => {
        const onLoadedData = () => {
            if (!isCancelled()) setPhase(AnimatedTilePhase.PLAYING);
        };
        const onError = () => {
            if (isCancelled()) return;
            setPhase(AnimatedTilePhase.FAILED);
            if (!player.isDisposed()) player.dispose();
        };
        player.on('loadeddata', onLoadedData);
        player.on('error', onError);
    };

    useEffect(() => {
        setPhase(AnimatedTilePhase.LOADING);
    }, [videoSrc]);

    useEffect(() => {
        if (!rootEl) return;

        let cancelled = false;
        rootEl.replaceChildren();

        const videoEl = document.createElement('video-js');
        videoEl.classList.add('animated-tile__player');
        videoEl.setAttribute('aria-label', `${gameName} animated tile`);
        rootEl.appendChild(videoEl);

        const videoType = detectVideoTypeFromSource(videoSrc);
        const source = videoType ? { src: videoSrc, type: videoType } : { src: videoSrc };

        let player: Player | undefined;

        // video.js is loaded on demand so it stays out of the initial bundle of every page that renders game tiles.
        import('video.js')
            .then(({ default: videojs }) => {
                if (cancelled) return;
                player = videojs(
                    videoEl,
                    {
                        ...PLAYER_DEFAULT_OPTIONS,
                        preload: priority ? 'auto' : 'metadata',
                        sources: [source]
                    },
                    function onReady(this: Player) {
                        attachPlayerListeners(this, () => cancelled);
                    }
                );
                playerRef.current = player;
            })
            .catch(() => {
                if (!cancelled) setPhase(AnimatedTilePhase.FAILED);
            });

        return () => {
            cancelled = true;
            playerRef.current = null;
            if (player && !player.isDisposed()) player.dispose();
        };
    }, [videoSrc, gameName, priority, rootEl]);

    useEffect(() => {
        const player = playerRef.current;
        if (!player || player.isDisposed() || phase !== AnimatedTilePhase.PLAYING) return;

        if (isIntersecting) player.play();
        else player.pause();
    }, [isIntersecting, phase]);

    if (phase === AnimatedTilePhase.FAILED) return <>{children}</>;

    const isPreload = phase === AnimatedTilePhase.LOADING;

    return (
        <>
            {isPreload ? children : null}
            <div
                ref={tileRef}
                className={classes('background', 'animated-tile', isPreload && 'animated-tile--preload')}
                data-vjs-player
            />
        </>
    );
};
